import * as React from "react";

// Define the available time formats
export enum TimeFormat {
  EPOCH = "Epoch Time (ms)",
  ISO = "ISO Format",
  HOUR_12 = "12-Hour Format",
  HOUR_24 = "24-Hour Format",
  MILITARY = "Military Time (UTC)"
}

// Interface for formatted time values
export interface FormattedTime {
  display: string | number;
  raw: number;
  format: TimeFormat;
  formatLabel: string;
}

/**
 * Formats a timestamp into various time representations
 * @param timestamp Epoch time in milliseconds
 * @param format The desired time format
 */
export function formatTime(timestamp: number, format: TimeFormat): FormattedTime {
  const date = new Date(timestamp);

  switch (format) {
    case TimeFormat.EPOCH:
      return {
        display: timestamp,
        raw: timestamp,
        format: TimeFormat.EPOCH,
        formatLabel: "EPOCH"
      };

    case TimeFormat.ISO:
      return {
        display: date.toISOString().replace('T', ' ').substring(0, 19),
        raw: timestamp,
        format: TimeFormat.ISO,
        formatLabel: "ISO"
      };

    case TimeFormat.HOUR_12: {
      const hours = date.getHours();
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const hour12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM

      return {
        display: `${hour12}:${minutes}:${seconds} ${ampm}`,
        raw: timestamp,
        format: TimeFormat.HOUR_12,
        formatLabel: "12HR"
      };
    }

    case TimeFormat.HOUR_24: {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const seconds = date.getSeconds().toString().padStart(2, '0');
      const milliseconds = date.getMilliseconds().toString().padStart(3, '0');

      return {
        display: `${hours}:${minutes}:${seconds}.${milliseconds}`,
        raw: timestamp,
        format: TimeFormat.HOUR_24,
        formatLabel: "24HR"
      };
    }

    case TimeFormat.MILITARY: {
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      const day = date.getUTCDate().toString().padStart(2, '0');
      const month = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                     'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'][date.getUTCMonth()];

      return {
        display: `${hours}${minutes}Z ${day} ${month}`,
        raw: timestamp,
        format: TimeFormat.MILITARY,
        formatLabel: "MIL"
      };
    }

    default:
      return {
        display: timestamp,
        raw: timestamp,
        format: TimeFormat.EPOCH,
        formatLabel: "EPOCH"
      };
  }
}

/**
 * Enhanced hook for smoothed epoch time with stabilization techniques
 * @param smoothingFactor Controls how quickly the displayed value approaches the target
 * @param roundingPrecision Controls the decimal precision for display stability
 * @returns A stable, smoothed epoch time value
 */
export function useSmoothedEpochTime(smoothingFactor = 0.1, roundingPrecision = 0) {
  const [targetEpochTime, setTargetEpochTime] = React.useState(Date.now());
  // State for the smoothed epoch time (displayed value)
  const [smoothedEpochTime, setSmoothedEpochTime] = React.useState(Date.now());

  // Update targetEpochTime at a modest frequency for better stability
  React.useEffect(() => {
    const intervalId = setInterval(() => {
      setTargetEpochTime(Date.now());
    }, 100);

    return () => clearInterval(intervalId);
  }, []);

  // Use memo for smoothed value to reduce re-renders
  const memoizedSmoothed = React.useMemo(() => {
    // Round to specified precision to reduce tiny movements that cause jitter
    return Math.round(smoothedEpochTime * (10 ** roundingPrecision)) / (10 ** roundingPrecision);
  }, [smoothedEpochTime, roundingPrecision]);

  // Smoothly update smoothedEpochTime using requestAnimationFrame with performance optimizations
  React.useEffect(() => {
    let animationFrameId: number;
    let lastTimestamp: number | null = null;

    const updateSmoothedValue = (timestamp: number) => {
      // Skip update if less than 16ms passed (targeting 60fps)
      if (lastTimestamp && timestamp - lastTimestamp < 16) {
        animationFrameId = requestAnimationFrame(updateSmoothedValue);
        return;
      }

      lastTimestamp = timestamp;

      // Calculate the difference and move a fraction towards the target
      setSmoothedEpochTime((prev) => {
        const delta = targetEpochTime - prev;
        // Apply a minimum threshold to prevent tiny updates that cause jitter
        if (Math.abs(delta) < 1) {
          return prev;
        }
        return prev + delta * smoothingFactor;
      });

      animationFrameId = requestAnimationFrame(updateSmoothedValue);
    };

    animationFrameId = requestAnimationFrame(updateSmoothedValue);
    return () => cancelAnimationFrame(animationFrameId);
  }, [targetEpochTime, smoothingFactor]);

  return memoizedSmoothed;
}

/**
 * Custom hook to handle clipboard copy keyboard shortcuts (Ctrl+C or Command+C)
 * @param callback Function to execute when the shortcut is pressed
 */
export function useCopyShortcut(callback: () => void) {
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Check for Ctrl+C or Command+C
      if (
        event.key.toLowerCase() === 'c' &&
        (event.ctrlKey || event.metaKey)
      ) {
        // Only prevent default if not in an input field to avoid interfering
        // with normal copy operations in editable fields
        const activeElement = document.activeElement;
        const isInput =
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement ||
          (activeElement?.getAttribute('contenteditable') === 'true');

        if (!isInput) {
          event.preventDefault();
          callback();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [callback]);
}

/**
 * Hook to cycle through different time format displays when spacebar is pressed
 * @returns Current time format and function to cycle formats
 */
export function useTimeFormatCycling() {
  const [currentFormat, setCurrentFormat] = React.useState<TimeFormat>(TimeFormat.EPOCH);

  // Create a function to cycle through formats
  const cycleFormat = React.useCallback(() => {
    setCurrentFormat((prevFormat) => {
      const formats = Object.values(TimeFormat);
      const currentIndex = formats.indexOf(prevFormat);
      const nextIndex = (currentIndex + 1) % formats.length;
      return formats[nextIndex];
    });
  }, []);

  // Listen for spacebar keypress to cycle formats
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === ' ' || event.code === 'Space') {
        // Only capture spacebar if not in an input field
        const activeElement = document.activeElement;
        const isInput =
          activeElement instanceof HTMLInputElement ||
          activeElement instanceof HTMLTextAreaElement ||
          (activeElement?.getAttribute('contenteditable') === 'true');

        if (!isInput) {
          event.preventDefault();
          cycleFormat();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [cycleFormat]);

  return { currentFormat, cycleFormat };
}